import * as React from "react";

import { DeleteWithConfirmationDialog } from "@impero-com/common/components/deleteWithConfirmationDialog/DeleteWithConfirmationDialog";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Button,
    emitNotification,
    InformationModal,
    renderMarkdown,
} from "@impero-com/ui";

import { deleteRisksRiskDirectoryIdNodesNodeId } from "api/generated/risks/risks";
import * as R from "org/risks/types";

import * as T from "../types";

interface DeleteNodeDialogProps {
    ajaxManager: AjaxManager;
    onDelete: (node: R.RiskDirectoryNode | R.StandardNode) => void;
    onCancel: () => void;
    riskDirectoryView: R.RiskDirectoryView;
    node: T.DirectoryNode;
    dialogType: DeleteNodeDialogType;
}

interface DeleteNodeDialogState {
    // Status avoid double-sending delete query
    status: DeleteNodeDialogStatus;
}

type DeleteNodeDialogStatus = { type: "DEFAULT" } | { type: "DELETING" };
type DeleteNodeDialogType =
    | { type: "WITH_CONFIRM_DIALOG" }
    | { type: "OK_CANCEL" };

class DeleteNodeDialog extends React.PureComponent<
    DeleteNodeDialogProps,
    DeleteNodeDialogState
> {
    constructor(props: DeleteNodeDialogProps) {
        super(props);
        this.state = {
            status: { type: "DEFAULT" },
        };
    }

    render = () => {
        const {
            ajaxManager,
            onDelete,
            riskDirectoryView,
            node,
            onCancel,
            dialogType,
        } = this.props;
        const onClickDelete = () => {
            ajaxManager
                .ajax<void, DeleteNodeDialogStatus>(
                    () =>
                        deleteRisksRiskDirectoryIdNodesNodeId(
                            riskDirectoryView.content.resource.id,
                            node.id
                        ),
                    {
                        component: this,
                        initialStatusValue: "DEFAULT",
                        getErrorStatus: (err) => {
                            emitNotification(
                                "error",
                                t("RiskDirectory_View_Delete_Title") +
                                    ": " +
                                    err
                            );
                            return { type: "DEFAULT" };
                        },
                        getSuccessStatus: () => {
                            return { type: "DEFAULT" };
                        },
                        inFlightStatus: { type: "DELETING" },
                    }
                )
                .then(() => {
                    onDelete(node);
                });
        };
        switch (dialogType.type) {
            case "WITH_CONFIRM_DIALOG":
                return (
                    <DeleteWithConfirmationDialog
                        title={t("RiskDirectory_View_Delete_Title")}
                        onConfirm={onClickDelete}
                        onCancel={onCancel}
                    >
                        {renderMarkdown(
                            t("RiskDirectory_NodeUpdate_Delete_Dialog_Body")
                        )}
                    </DeleteWithConfirmationDialog>
                );
            case "OK_CANCEL":
                return (
                    <InformationModal
                        open
                        onClose={onCancel}
                        type="error"
                        icon="delete"
                        title={t("RiskDirectory_View_Delete_Title")}
                        footer={
                            <>
                                <Button onClick={onCancel}>
                                    {t("Cancel")}
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={onClickDelete}
                                >
                                    {t("Delete")}
                                </Button>
                            </>
                        }
                    >
                        {renderMarkdown(
                            t("RiskMapEditStructure_DeletePopup_Text")
                        )}
                    </InformationModal>
                );
            default:
                return assertNever(dialogType);
        }
    };
}

export { DeleteNodeDialog, type DeleteNodeDialogType };
